import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProducts } from '../hooks/useProducts';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Gaming = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('tr') ? 'tr' : 'en';
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || null);
  const { products: gamingProducts, loading } = useProducts('gaming');
  const [pageContent, setPageContent] = useState(null);
  const [hfContent, setHfContent] = useState(null);
  const productsRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/public/content/gaming`).then(res => {
      if (res.data) setPageContent(res.data);
    }).catch(() => {});
    axios.get(`${API}/api/public/content/header_footer`).then(res => {
      if (res.data && Object.keys(res.data).length > 0) setHfContent(res.data);
    }).catch(() => {});
  }, []);

  // Scroll to products section when arriving with ?category= param
  useEffect(() => {
    if (selectedCategory && productsRef.current) {
      productsRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  }, []);

  // Build categories dynamically from admin content, with product data
  const categories = (pageContent?.categories || [
    { key: 'Gaming Desks', label: { en: 'Gaming Desks', tr: 'Oyuncu Masaları' }, desc: { en: 'Premium gaming desk collection', tr: 'Premium oyuncu masası koleksiyonu' }, image: '' },
    { key: 'Gaming Monitor Arms', label: { en: 'Monitor Arms', tr: 'Monitör Kolları' }, desc: { en: 'Ergonomic monitor arms for your gaming setup', tr: 'Oyun kurulumunuz için ergonomik monitör kolları' }, image: '' },
    { key: 'Gaming Accessories', label: { en: 'Accessories', tr: 'Aksesuarlar' }, desc: { en: 'Accessories for your gaming setup', tr: 'Oyun kurulumunuz için aksesuarlar' }, image: '' }
  ]).map(cat => {
    const prods = gamingProducts.filter(p => p.category === cat.key).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    const label = typeof cat.label === 'object' ? (cat.label[lang] || cat.label.en || '') : cat.label;
    const desc = typeof cat.desc === 'object' ? (cat.desc[lang] || cat.desc.en || '') : cat.desc;
    const image = cat.image || prods[0]?.cardImage || prods[0]?.images?.[0] || '';
    return { key: cat.key, label, desc, image, products: prods };
  });

  // Get filtered products based on selected category
  const getFilteredProducts = () => {
    const category = categories.find(c => c.key === selectedCategory);
    return category ? category.products : [];
  };

  const filteredProducts = getFilteredProducts();

  // Helper to get localized text
  const getText = (field) => {
    if (typeof field === 'object' && field !== null) {
      return field[lang] || field.en || '';
    }
    return field || '';
  };

  useEffect(() => { document.title = 'VIYERO Gaming | Oyuncu Ekipmanları'; return () => { document.title = 'VIYERO | Ergonomic Solutions'; }; }, []);

  return (
    <div className="min-h-screen bg-gray-950">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-16 md:pt-20 pb-8 md:pb-10 overflow-hidden">
        {!pageContent?.hero_image_hidden && (
          <div className="absolute inset-0">
            <img
              src={pageContent?.hero_image || ""}
              alt="Gaming Hero"
              loading="eager"
              decoding="async"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-gray-950/90 to-gray-950" />
          </div>
        )}

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* VIYERO Gaming Logo - moved to top of hero */}
          <div className="flex justify-center mb-4">
            <img
              src={hfContent?.gaming_logo || "/viyero-gaming-logo.webp"}
              alt="VIYERO Gaming"
              className="h-36 md:h-[11.25rem] w-auto object-contain filter drop-shadow-2xl"
            />
          </div>
          
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight">
              {lang === 'tr' ? (
                <>
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">{pageContent?.hero_title?.[lang] || t('gaming.heroTitle')}</span> {pageContent?.hero_highlight?.[lang] || t('gaming.heroHighlight')}
                </>
              ) : (
                <>
                  {pageContent?.hero_title?.[lang] || t('gaming.heroTitle')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">{pageContent?.hero_highlight?.[lang] || t('gaming.heroHighlight')}</span>
                </>
              )}
            </h1>
            <p className="text-lg md:text-xl text-gray-400">
              {pageContent?.hero_description?.[lang] || t('gaming.heroDesc')}
            </p>
          </div>
        </div>
      </section>

      {/* Categories / Products Section */}
      <section ref={productsRef} className="py-6 md:py-8 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Back Button - Show when category selected */}
          {selectedCategory && (
            <button
              onClick={() => setSelectedCategory(null)}
              className="flex items-center gap-2 text-purple-400 hover:text-purple-300 mb-6 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm font-medium">
                {lang === 'tr' ? 'Tüm Kategoriler' : 'All Categories'}
              </span>
            </button>
          )}

          {/* Category Title when selected */}
          {selectedCategory && (
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {categories.find(c => c.key === selectedCategory)?.label}
              </h2>
              <p className="text-gray-400 text-lg">
                {categories.find(c => c.key === selectedCategory)?.desc}
              </p>
            </div>
          )}

          {/* Category Cards - Show when no category selected */}
          {!selectedCategory && (
            <div className="flex flex-wrap justify-center gap-6 md:gap-8">
              {categories.map((cat) => (
                <div 
                  key={cat.key} 
                  onClick={() => { setSelectedCategory(cat.key); productsRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' }); }}
                  className="cursor-pointer group w-full sm:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]"
                >
                  <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 bg-gray-900 border-gray-800">
                    <div className="relative overflow-hidden aspect-[4/3]">
                      <img
                        src={cat.image}
                        alt={cat.label}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-4 bg-black">
                      <h3 className="text-lg font-bold text-gray-300 mb-1">{cat.label}</h3>
                      <p className="text-gray-500 text-sm mb-3">{cat.desc}</p>
                      <div className="flex items-center justify-end">
                        <ArrowRight className="h-5 w-5 text-purple-400 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          )}

          {/* Products Grid - Show when category selected */}
          {selectedCategory && (
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              {filteredProducts.map((product) => (
                <Link key={product.id} to={`/product/${product.id}`} className="group w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]">
                  <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 bg-gray-900 border-gray-800 transform hover:-translate-y-1">
                    <div className="relative overflow-hidden aspect-[4/3]">
                      <img
                        src={product.gamingCardImage || product.cardImage || product.images[0]}
                        alt={getText(product.displayName) || product.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                    <CardContent className="p-6 bg-black">
                      <div className="mb-2">
                        <span className="text-base font-medium text-purple-400 uppercase tracking-wide">
                          {product.model}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-300 mb-2 whitespace-pre-line">
                        {getText(product.displayName) || product.name}
                      </h3>
                      <Button variant="ghost" className="group/btn p-0 h-auto hover:bg-transparent text-purple-400">
                        <span className="text-sm font-medium">{lang === 'tr' ? 'Detaylar' : 'View Details'}</span>
                        <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Button>
                    </CardContent>
                  </Card>
                </Link>
              ))}
              {filteredProducts.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-500 text-lg">
                    {lang === 'tr' ? 'Bu kategoride henüz ürün yok.' : 'No products in this category yet.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      {!pageContent?.features_hidden && (
      <section className="py-12 md:py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {(pageContent?.features_title?.[lang] || pageContent?.features_title?.en) || t('gaming.featuresTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {(pageContent?.features || [
              { icon: '⚡', title: { en: t('gaming.feature1'), tr: t('gaming.feature1') }, desc: { en: t('gaming.feature1Desc'), tr: t('gaming.feature1Desc') } },
              { icon: '🎮', title: { en: t('gaming.feature2'), tr: t('gaming.feature2') }, desc: { en: t('gaming.feature2Desc'), tr: t('gaming.feature2Desc') } },
              { icon: '🛡️', title: { en: t('gaming.feature3'), tr: t('gaming.feature3') }, desc: { en: t('gaming.feature3Desc'), tr: t('gaming.feature3Desc') } }
            ]).map((feat, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                  <span className="text-2xl text-white">{feat.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {(typeof feat.title === 'object' ? feat.title[lang] || feat.title.en : feat.title) || ''}
                </h3>
                <p className="text-gray-400">
                  {(typeof feat.desc === 'object' ? feat.desc[lang] || feat.desc.en : feat.desc) || ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      <Footer />
    </div>
  );
};

export default Gaming;
